import { Box, Icon, Text, ToastId, UseToastOptions } from '@chakra-ui/react';
import { isEmpty } from 'ramda';
import { BiCheckCircle, BiErrorCircle } from 'react-icons/bi';
import {
  ToastNotification,
  ToastNotificationProps,
} from '../../ui/toast-notification/toast-notification';

import { EnhancedFile } from '../../utils/file/file';

export type FailedUploads = Array<[string, string?]>;

export const Notification = {
  uploading: (props: ToastNotificationProps): UseToastOptions => ({
    duration: null,
    position: 'bottom',
    render: () => (
      <ToastNotification {...props} data-testid="uploading-notification" />
    ),
  }),
  uploadSuccess: (props: ToastNotificationProps): UseToastOptions => ({
    position: 'bottom',
    render: () => (
      <ToastNotification {...props} data-testid="upload-success-notification" />
    ),
  }),
  uploadIncomplete: (props: ToastNotificationProps): UseToastOptions => ({
    duration: 10_000,
    position: 'bottom',
    render: () => (
      <ToastNotification
        {...props}
        data-testid="upload-incomplete-notification"
      />
    ),
  }),
  uploadError: (props: ToastNotificationProps): UseToastOptions => ({
    position: 'bottom',
    duration: 10_000,
    render: () => (
      <ToastNotification {...props} data-testid="upload-error-notification" />
    ),
  }),
};

export const notifyUploadResult = (
  failedUploads: FailedUploads,
  files: EnhancedFile[],
  showToast: (options: UseToastOptions, id?: ToastId) => void,
  toastId: ToastId
): void => {
  const [fileCount, failedCount] = [files.length, failedUploads.length];
  const uploadError = failedCount === fileCount;
  const uploadIncomplete = !isEmpty(failedUploads) && failedCount !== fileCount;

  if (uploadError) {
    const uploadErrorNotification = Notification.uploadError({
      size: 'full-width',
      variant: 'error',
      subtitle: 'Please try again',
      icon: <Icon as={BiErrorCircle} fontSize="1.4rem" />,
      title: `Failed to upload ${failedCount} files`,
      panel: {
        ctaLabel: 'Details',
        body: failedUploads.map(([filename, error]) => (
          <Box key={filename}>
            <Text fontSize="sm" fontWeight="500">
              {filename} {error && `- ${error}`}
            </Text>
          </Box>
        )),
      },
    });
    return showToast(uploadErrorNotification, toastId);
  }

  if (uploadIncomplete) {
    const filesUploadedCount = fileCount - failedCount;
    const incompleteUploadNotification = Notification.uploadIncomplete({
      size: 'full-width',
      variant: 'warning',
      icon: <Icon as={BiErrorCircle} fontSize="1.4rem" />,
      subtitle: `Failed to upload ${failedCount} files`,
      title: `${filesUploadedCount} files uploaded successfully`,
      panel: {
        ctaLabel: 'Details',
        body: failedUploads.map(([filename, error]) => (
          <Box key={filename}>
            <Text fontSize="sm" fontWeight="500">
              {filename} {error && `- ${error}`}
            </Text>
          </Box>
        )),
      },
    });
    return showToast(incompleteUploadNotification, toastId);
  }

  const uploadSuccesNotification = Notification.uploadSuccess({
    size: 'full-width',
    icon: <Icon as={BiCheckCircle} fontSize="1.4rem" />,
    title: `${fileCount} files uploaded successfully`,
  });
  showToast(uploadSuccesNotification, toastId);
};
